import { useState, useEffect } from 'react';
import { useAppStore } from '../../store/appStore';
import { aiContextService } from '../../services/ai-context.service';
import { translationService } from '../../services/translation.service';
import './AIContextPanel.css';

export const AIContextPanel = () => {
  const { currentGesture, currentEmotion, isAuthenticated } = useAppStore();
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const [translation, setTranslation] = useState<string>('');
  const [targetLanguage, setTargetLanguage] = useState('es');
  const [isLoading, setIsLoading] = useState(false);
  const [languages, setLanguages] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    translationService.initialize().then(() => {
      setLanguages(translationService.getSupportedLanguages());
      setTargetLanguage(translationService.getCurrentLanguage());
    });
  }, []);

  useEffect(() => {
    if (currentGesture && isAuthenticated) {
      getAISuggestion(currentGesture.gesture);
    }
  }, [currentGesture, isAuthenticated]);

  const getAISuggestion = async (gesture: string) => {
    setIsLoading(true);
    try {
      const suggestion = await aiContextService.getContextSuggestion(gesture);
      if (suggestion) {
        setAiSuggestion(suggestion);
        
        // Auto-translate if target language is not English
        if (targetLanguage !== 'en') {
          const translated = await translationService.translate(suggestion, targetLanguage);
          setTranslation(translated);
        }
      }
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranslate = async () => {
    if (!aiSuggestion) return;
    
    setIsLoading(true);
    try {
      const translated = await translationService.translate(aiSuggestion, targetLanguage);
      setTranslation(translated);
    } catch (error) {
      console.error('Translation failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (lang: string) => {
    setTargetLanguage(lang);
    translationService.setCurrentLanguage(lang);
    if (aiSuggestion) {
      handleTranslate();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="ai-context-panel">
        <div className="auth-required">
          <p>Login to enable AI context suggestions and translation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-context-panel">
      <h3>AI Context & Translation</h3>

      {currentGesture && (
        <div className="current-gesture-info">
          <span className="gesture-label">Current Gesture:</span>
          <span className="gesture-value">{currentGesture.gesture}</span>
        </div>
      )}

      {isLoading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <span>Processing...</span>
        </div>
      )}

      {aiSuggestion && !isLoading && (
        <div className="ai-suggestion">
          <h4>AI Suggestion:</h4>
          <p className="suggestion-text">{aiSuggestion}</p>
          
          {currentEmotion && (
            <div className="emotion-tag">
              Emotion: {currentEmotion.emotion}
            </div>
          )}
        </div>
      )}

      <div className="translation-section">
        <div className="language-selector">
          <label>Translate to:</label>
          <select value={targetLanguage} onChange={(e) => handleLanguageChange(e.target.value)}>
            {Object.entries(languages).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </div>

        {translation && targetLanguage !== 'en' && (
          <div className="translation-result">
            <h4>Translation ({languages[targetLanguage]}):</h4>
            <p className="translation-text">{translation}</p>
          </div>
        )}
      </div>

      <div className="conversation-history">
        <h4>Conversation History:</h4>
        <div className="history-list">
          {aiContextService.getHistory().slice(-5).map((item, index) => (
            <div key={index} className="history-item">
              {item}
            </div>
          ))}
        </div>
        <button 
          onClick={() => aiContextService.clearHistory()}
          className="btn btn-sm btn-secondary"
        >
          Clear History
        </button>
      </div>
    </div>
  );
};
